import type {
  ReportBoard,
  ReportBoardRequest,
  ReportTile
} from "@gettactic/api";
import { useInvalidateReportBoards } from "@gettactic/hooks";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api/api";

const unwrap = <T>(result: T | null, what: string): T => {
  if (result === null) {
    throw new Error(`empty ${what} response`);
  }
  return result;
};

const NOT_UNDERSTOOD = 422;
const UNAVAILABLE = 503;

/** Building a board from a request failed; the status says why. */
export class ComposeBoardError extends Error {
  constructor(public readonly status: number) {
    super(`compose board failed with ${status}`);
  }
}

export const composeErrorMessage = (error: unknown): string => {
  if (error instanceof ComposeBoardError) {
    if (error.status === NOT_UNDERSTOOD) {
      return "Nothing to build a board from — name a place or a topic, like desks, rooms or requests.";
    }
    if (error.status === UNAVAILABLE) {
      return "Building boards from a request isn't available right now.";
    }
  }
  return "The board could not be built.";
};

/** Every board write, each refreshing the boards list when it lands. */
export const useReportBoardMutations = () => {
  const invalidate = useInvalidateReportBoards();

  const createBoard = useMutation({
    mutationFn: async (body: ReportBoardRequest): Promise<ReportBoard> => {
      const { result } = await api.client.reporting.createBoard(body);
      return unwrap(result, "create board");
    },
    onSuccess: invalidate
  });

  // One request, one saved board: the standard questions with something
  // to show for the place or topic it names.
  const composeBoard = useMutation({
    mutationFn: async (question: string): Promise<ReportBoard> => {
      const { result, status } =
        await api.client.reporting.composeBoard(question);
      if (!result) {
        throw new ComposeBoardError(status);
      }
      return result;
    },
    onSuccess: invalidate
  });

  const updateBoard = useMutation({
    mutationFn: async (input: {
      id: string;
      body: ReportBoardRequest;
    }): Promise<ReportBoard> => {
      const { result } = await api.client.reporting.updateBoard(
        input.id,
        input.body
      );
      return unwrap(result, "update board");
    },
    onSuccess: invalidate
  });

  const updateTiles = useMutation({
    mutationFn: async (input: {
      id: string;
      tiles: ReportTile[];
    }): Promise<ReportBoard> => {
      const { result } = await api.client.reporting.updateBoardTiles(
        input.id,
        input.tiles
      );
      return unwrap(result, "update board tiles");
    },
    onSuccess: invalidate
  });

  const deleteBoard = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.client.reporting.deleteBoard(id);
    },
    onSuccess: invalidate
  });

  const duplicateDefault = useMutation({
    mutationFn: async (key: string): Promise<ReportBoard> => {
      const { result } = await api.client.reporting.duplicateDefaultBoard(key);
      return unwrap(result, "duplicate board");
    },
    onSuccess: invalidate
  });

  return {
    createBoard,
    composeBoard,
    updateBoard,
    updateTiles,
    deleteBoard,
    duplicateDefault
  };
};
